import React, { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleOutline,
  AccountBalance,
  CloudUpload,
  EmojiEvents,
  WavingHand,
} from '@mui/icons-material';
import { useNavigate } from '@tanstack/react-router';
import { accountsService } from '../services/accounts-service';
import type { AccountType, Currency } from '../types/api';

interface OnboardingWizardProps {
  /** Called when the user completes the onboarding wizard. */
  onComplete: () => void;
  /** Called when the user skips the onboarding wizard. */
  onSkip: () => void;
}

/** Steps configuration for the onboarding wizard. */
const STEPS = [
  'Welcome',
  'Create Household',
  'Add First Account',
  'Import Transactions',
  "You're All Set",
];

const ACCOUNT_TYPES: AccountType[] = ['Asset', 'Debt', 'Investment'];
const CURRENCIES: Currency[] = ['CAD', 'USD', 'EUR', 'GBP', 'INR', 'AUD', 'CHF', 'JPY', 'CNY'];

/**
 * Multi-step onboarding wizard shown on first login when no accounts exist.
 *
 * Guides the user through creating a household, adding their first account,
 * and importing transactions. Uses MUI Stepper with vertical layout on mobile
 * and horizontal layout on desktop.
 */
export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onComplete,
  onSkip,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState<AccountType>('Asset');
  const [currency, setCurrency] = useState<Currency>('CAD');
  const [institution, setInstitution] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleFinish = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onSkip();
  };

  /**
   * Creates a new account via the API.
   * On success, advances to the next step.
   */
  const handleCreateAccount = async () => {
    if (!accountName.trim()) {
      setCreateError('Account name is required.');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await accountsService.createAccount({
        name: accountName.trim(),
        account_type: accountType,
        currency,
        institution: institution.trim() || undefined,
      });
      setAccountCreated(true);
      handleNext();
    } catch (err: any) {
      setCreateError(
        err?.message || 'Failed to create account. Please try again.'
      );
    } finally {
      setCreating(false);
    }
  };

  const handleNavigateToImport = () => {
    localStorage.setItem('onboarding_completed', 'true');
    navigate({ to: '/import-export' });
  };

  /** Renders the content for each step of the wizard. */
  const renderStepContent = (step: number): React.ReactNode => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WavingHand sx={{ fontSize: 32, color: 'warning.main' }} />
              <Typography variant="h5" fontWeight="bold">
                Welcome to FolioWorth
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              FolioWorth helps you track your net worth, manage financial
              accounts, and gain insights into your financial health.
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Track multiple accounts across currencies
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Visualize your net worth over time with interactive charts
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Manage investments with cost-basis tracking
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Set budgets and financial goals
              </Typography>
              <Typography component="li" variant="body2">
                Import transactions from CSV files
              </Typography>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Create Your Household
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              A household has been automatically created for your account. You
              can invite family members later from the Household settings page.
            </Typography>
            <Alert severity="info" sx={{ mt: 1 }}>
              Your household is already set up. You can skip this step and
              manage household members later under Settings.
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccountBalance color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Add Your First Account
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add a financial account to start tracking. You can add more
              accounts later.
            </Typography>

            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}

            {accountCreated ? (
              <Alert severity="success">
                Account created successfully! Moving to the next step.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  label="Account Name"
                  placeholder="e.g. Savings Account, Credit Card"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <FormControl fullWidth size="small">
                  <InputLabel id="account-type-label">Account Type</InputLabel>
                  <Select
                    labelId="account-type-label"
                    value={accountType}
                    label="Account Type"
                    onChange={(e) =>
                      setAccountType(e.target.value as AccountType)
                    }
                  >
                    {ACCOUNT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Select
                    labelId="currency-label"
                    value={currency}
                    label="Currency"
                    onChange={(e) => setCurrency(e.target.value as Currency)}
                  >
                    {CURRENCIES.map((cur) => (
                      <MenuItem key={cur} value={cur}>
                        {cur}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Institution (optional)"
                  placeholder="e.g. TD Bank, Wealthsimple"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleCreateAccount}
                  disabled={creating || !accountName.trim()}
                  startIcon={
                    creating ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AccountBalance />
                    )
                  }
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {creating ? 'Creating...' : 'Create Account'}
                </Button>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CloudUpload color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Import Transactions
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              You can import transactions from CSV files to quickly populate
              your account history. FolioWorth supports standard CSV formats
              from most financial institutions.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You can always import transactions later from the Import/Export
              page.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleNavigateToImport}
              startIcon={<CloudUpload />}
            >
              Go to Import Page
            </Button>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EmojiEvents sx={{ fontSize: 32, color: 'success.main' }} />
              <Typography variant="h5" fontWeight="bold">
                You're All Set!
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              You're ready to start tracking your financial health with
              FolioWorth. Here are some next steps:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Take a snapshot to record your current balances
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Add more accounts to get a complete picture
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                Set up budgets and financial goals
              </Typography>
              <Typography component="li" variant="body2">
                Explore reports and charts on the Dashboard
              </Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  // Reason: Vertical stepper on mobile provides better readability with step content inline;
  // horizontal stepper on desktop uses space more efficiently.
  if (isMobile) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 2 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {STEPS.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>{renderStepContent(index)}</Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {index > 0 && (
                    <Button onClick={handleBack} size="small">
                      Back
                    </Button>
                  )}
                  {index < STEPS.length - 1 ? (
                    <>
                      {/* Reason: Skip button on step 2 only if account not yet created */}
                      {index === 2 && !accountCreated && (
                        <Button
                          onClick={handleNext}
                          size="small"
                          color="inherit"
                        >
                          Skip
                        </Button>
                      )}
                      {index !== 2 && (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          size="small"
                        >
                          Next
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleFinish}
                      size="small"
                      startIcon={<CheckCircleOutline />}
                    >
                      Get Started
                    </Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button onClick={handleSkip} size="small" color="inherit">
                    Skip All
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    );
  }

  // Desktop: horizontal stepper with content below
  return (
    <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 250, mb: 3 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2,
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleSkip} color="inherit">
            Skip All
          </Button>
          {activeStep === 2 && !accountCreated ? (
            <Button
              onClick={handleNext}
              color="inherit"
            >
              Skip Step
            </Button>
          ) : activeStep < STEPS.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleFinish}
              startIcon={<CheckCircleOutline />}
            >
              Get Started
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
